import { MoreVertOutlined } from '@mui/icons-material';
import { Buffer } from 'buffer';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { demeterPresentationTemplatesApi } from '../../../Apis/Apis';
import scssVariables from '../../../Config.module.scss';
import { DemeterUserSummaryModel, PresentationTemplateModel } from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import useApiWithoutAutoExecute from '../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonSize, LinkButtonType } from '../../Components/Form/Buttons/LinkButton';
import IconMenuMultiStageDropdown, { INestedOption } from '../../Components/Form/Inputs/IconMenuMultiStageDropdown';
import MultiSelectInput, { IMultiSelectInputOption } from '../../Components/Form/Inputs/MultiSelectInput';
import TextInput from '../../Components/Form/Inputs/TextInput';
import ActionModal from '../../Components/Modals/ActionModal/ActionModal';
import ConfirmModal from '../../Components/Modals/ConfirmModal/ConfirmModal';
import browserService from '../../Services/BrowserService';
import useLanguage from '../../Services/Language/useLanguageHook';
import styles from './PresentationTemplatesPage.module.scss';

interface IPresentationTemplateActionsProps {
    presentationTemplate?: PresentationTemplateModel;
    users?: DemeterUserSummaryModel[];
    handleRefresh: () => void;
}

enum PresentationTemplateAction {
    Download = 'Download',
    Edit = 'Edit',
    Delete = 'Delete',
    Duplicate = 'Duplicate',
    Share = 'Share',
}

const PresentationTemplateActions: React.FC<IPresentationTemplateActionsProps> = (props: IPresentationTemplateActionsProps) => {
    const [translations] = useLanguage();
    const navigate = useNavigate();
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [duplicateName, setDuplicateName] = useState<string>();
    const [selectedUserOptions, setSelectedUserOptions] = useState<IMultiSelectInputOption[]>([]);
    const [userOptions, setUserOptions] = useState<IMultiSelectInputOption[]>([]);
    const [menuOptions, setMenuOptions] = useState<INestedOption<PresentationTemplateAction>[]>([]);

    const [, downloadPresentationTemplate, downloadPresentationTemplateResponse] = useApiWithoutAutoExecute(() => {
        if (!props.presentationTemplate) {
            return null;
        }
        return demeterPresentationTemplatesApi.downloadPresentationTemplate(props.presentationTemplate.presentationTemplateGuid);
    });

    const [, deletePresentationTemplate, deletePresentationTemplateResponse] = useApiWithoutAutoExecute(() => {
        if (!props.presentationTemplate) {
            return null;
        }
        return demeterPresentationTemplatesApi.deletePresentationTemplate({ presentationTemplateGuid: props.presentationTemplate.presentationTemplateGuid });
    });

    const [, duplicatePresentationTemplate, duplicatePresentationTemplateResposne] = useApiWithoutAutoExecute(() => {
        if (!props.presentationTemplate || !duplicateName) {
            return null;
        }
        return demeterPresentationTemplatesApi.clonePresentationTemplate({
            presentationTemplateGuid: props.presentationTemplate.presentationTemplateGuid,
            name: duplicateName,
        });
    });

    const [, sharePresentationTemplate] = useApiWithoutAutoExecute(() => {
        if (!props.presentationTemplate || userOptions.length === 0) {
            return null;
        }
        return demeterPresentationTemplatesApi.sharePresentationTemplate({
            presentationTemplateGuid: props.presentationTemplate.presentationTemplateGuid,
            userGuids: selectedUserOptions.map((u) => u.id),
        });
    });

    useEffect(() => {
        const rows = props.users ?? [];
        const options: IMultiSelectInputOption[] = rows
            .filter((user) => !!user.demeterUserGuid)
            .map((user) => ({
                id: user.demeterUserGuid!,
                label: user.email ?? '',
            }));
        setUserOptions(options);
    }, [props.users]);

    useEffect(() => {
        if (!downloadPresentationTemplateResponse?.content || !props.presentationTemplate) {
            return;
        }
        const byteArray = Buffer.from(downloadPresentationTemplateResponse.content, 'base64');
        const blob = new Blob([new Uint8Array(byteArray)], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        browserService.downloadBlob(blob, `${props.presentationTemplate.name}.pptx`);
    }, [downloadPresentationTemplateResponse]);

    useEffect(() => {
        if (!deletePresentationTemplateResponse) {
            return;
        }
        if (props.handleRefresh) {
            props.handleRefresh();
        }
    }, [deletePresentationTemplateResponse]);

    useEffect(() => {
        if (!duplicatePresentationTemplateResposne || !duplicatePresentationTemplateResposne.presentationTemplateGuid) {
            return;
        }
        const editPath = NavigationRoutes.PresentationTemplatesEdit.replace(
            ':presentationTemplateGuid',
            duplicatePresentationTemplateResposne.presentationTemplateGuid,
        );
        navigate(editPath);
    }, [duplicatePresentationTemplateResposne]);

    useEffect(() => {
        if (!props.presentationTemplate) {
            return;
        }

        if (props.presentationTemplate.isShared) {
            setMenuOptions([
                {
                    key: PresentationTemplateAction.Duplicate,
                    label: translations.actions.duplicate,
                    value: PresentationTemplateAction.Duplicate,
                },
                {
                    key: PresentationTemplateAction.Delete,
                    label: translations.actions.delete,
                    value: PresentationTemplateAction.Delete,
                },
                {
                    key: PresentationTemplateAction.Download,
                    label: translations.actions.download,
                    value: PresentationTemplateAction.Download,
                },
            ]);
            return;
        }

        setMenuOptions([
            {
                key: PresentationTemplateAction.Edit,
                label: translations.actions.edit,
                value: PresentationTemplateAction.Edit,
            },
            {
                key: PresentationTemplateAction.Duplicate,
                label: translations.actions.duplicate,
                value: PresentationTemplateAction.Duplicate,
            },
            {
                key: PresentationTemplateAction.Share,
                label: translations.actions.share,
                value: PresentationTemplateAction.Share,
            },
            {
                key: PresentationTemplateAction.Delete,
                label: translations.actions.delete,
                value: PresentationTemplateAction.Delete,
            },
            {
                key: PresentationTemplateAction.Download,
                label: translations.actions.download,
                value: PresentationTemplateAction.Download,
            },
        ]);
    }, [props.presentationTemplate]);

    const handleDownload = () => {
        if (!props.presentationTemplate) {
            return;
        }
        downloadPresentationTemplate(props.presentationTemplate?.presentationTemplateGuid);
    };

    const handleEdit = () => {
        if (!props.presentationTemplate) {
            return;
        }
        const editPath = NavigationRoutes.PresentationTemplatesEdit.replace(':presentationTemplateGuid', props.presentationTemplate.presentationTemplateGuid);
        navigate(editPath);
    };

    const handleDelete = () => {
        if (!props.presentationTemplate) {
            return;
        }
        setShowConfirmDeleteModal(true);
    };

    const handleDuplicate = () => {
        if (!props.presentationTemplate) {
            return;
        }
        setShowDuplicateModal(true);
    };

    const handleShare = () => {
        if (!props.presentationTemplate) {
            return;
        }
        setShowShareModal(true);
    };

    const handleAction = (action: PresentationTemplateAction) => {
        switch (action) {
            case PresentationTemplateAction.Download:
                handleDownload();
                break;
            case PresentationTemplateAction.Edit:
                handleEdit();
                break;
            case PresentationTemplateAction.Delete:
                handleDelete();
                break;
            case PresentationTemplateAction.Duplicate:
                handleDuplicate();
                break;
            case PresentationTemplateAction.Share:
                handleShare();
                break;
            default:
                break;
        }
    };

    return (
        <>
            <div className={styles.presentation_templates_actions_cell}>
                <LinkButton
                    title={translations.actions.download}
                    type={LinkButtonType.White}
                    size={LinkButtonSize.Small}
                    onClick={() => handleDownload()}
                    track={{ eventCategory: 'PresentationTemplates', eventAction: 'DownloadClicked' }}
                />
                <IconMenuMultiStageDropdown
                    name="PresentationTemplateActionsMenu"
                    tooltip={translations.presentationTemplates.text.moreOptions}
                    iconButton={<MoreVertOutlined sx={{ color: scssVariables.mainTextColor }} />}
                    handleSelection={(action) => handleAction(action)}
                    options={menuOptions}
                    track={{ eventCategory: 'PresentationTemplates', eventAction: 'MoreOptions' }}
                />
            </div>
            <ConfirmModal
                header={translations.presentationTemplates.text.confirmDeleteHeader}
                message={translations.presentationTemplates.text.confirmDeleteMessage}
                showModal={showConfirmDeleteModal}
                handleConfirm={() => {
                    deletePresentationTemplate();
                    setShowConfirmDeleteModal(false);
                }}
                handleCancel={() => {
                    setShowConfirmDeleteModal(false);
                }}
                track={{ eventCategory: 'PresentationTemplates', eventAction: 'ModalDeleteConfirm' }}
            />
            <ActionModal
                header={translations.presentationTemplates.text.confirmDuplicateHeader}
                showModal={showDuplicateModal}
                actionButtonName={translations.actions.save}
                handleAction={() => {
                    duplicatePresentationTemplate();
                    setShowDuplicateModal(false);
                }}
                handleCancel={() => setShowDuplicateModal(false)}
                actionButtonDisabled={!duplicateName}
                track={{ eventCategory: 'PresentationTemplates', eventAction: 'ModalDuplicate' }}
            >
                <>
                    <div>{translations.presentationTemplates.fields.templateName}</div>
                    <TextInput
                        value={duplicateName}
                        handleTextChange={setDuplicateName}
                        required
                        track={{ eventCategory: 'PresentationTemplates', eventAction: 'ModalDuplicateNameChanged' }}
                    />
                </>
            </ActionModal>
            <ActionModal
                header={translations.presentationTemplates.text.confirmShareHeader}
                showModal={showShareModal}
                actionButtonName={translations.actions.share}
                handleAction={() => {
                    sharePresentationTemplate();
                    setShowShareModal(false);
                }}
                handleCancel={() => setShowShareModal(false)}
                actionButtonDisabled={selectedUserOptions.length === 0}
                track={{ eventCategory: 'PresentationTemplates', eventAction: 'ModalShare' }}
            >
                <div className={styles.presentation_templates_share_modal_content}>
                    <div>
                        <p>{translations.presentationTemplates.text.shareDescription}</p>
                    </div>
                    <div>
                        <strong>{translations.presentationTemplates.fields.users}</strong>
                    </div>
                    <MultiSelectInput options={userOptions} handleChange={setSelectedUserOptions} required />
                </div>
            </ActionModal>
        </>
    );
};
export default PresentationTemplateActions;
